import React from "react";
import * as R from "ramda";
import { css } from "glamor";
import { Query } from "react-apollo";

import Table from "@arachnys/styleguide/Table";
import { DocumentListWithFetch } from "../../container/Documents";
import {
    withApolloState,
    getValueFromPath,
    getJsonSchemaFromPath,
    getSourceByPath,
    getCountrySource,
} from "../../util";
import { formatWithJsonSchema } from "../../formatters";
import { DEFAULT_PAGESIZE_S } from "../../config";
import { GET_PROCESS_DISPOSITION } from "./queries";
import { prepareEvent } from "./NarrativeTable";

const styles = {
    category: css({
        display: "flex",
        padding: "10px 15px",
        width: "100%",
        backgroundColor: "#fafafa",
        borderWidth: "1px 0",
        borderStyle: "solid none",
        borderColor: "#e8e7e8",
        fontSize: 14,
        marginBottom: 15,
    }),
    table: css({
        marginBottom: 25,
    }),
    thead: css({
        backgroundColor: "#e5f5fc",
        border: "none",
        marginBottom: 15,
    }),
    theadTh: normalFirstTitle =>
        css({
            fontWeight: "normal",
            ...(!normalFirstTitle && {
                "&:first-child": {
                    fontSize: 20,
                },
            }),
        }),
    trGroup: css({
        borderBottom: "none !important",
    }),
    td: css({
        "&:nth-child(2)": {
            borderRight: "1px dashed #ebebeb !important",
        },
    }),
};

export const Category = ({ children }) => (
    <div className={styles.category}>{children}</div>
);

const formatArrayValues = values =>
    values.length ? (
        <ul style={{ paddingLeft: "1em" }}>
            {values.map(value =>
                typeof value === "object" ? (
                    <li key={value.id}>
                        {value.type} ({value.id})
                    </li>
                ) : (
                    <li key={value}>{value}</li>
                )
            )}
        </ul>
    ) : (
        ""
    );

const generateProcessIds = ids => ids.map(val => `process:${val}`).join(",");

const prepareTableData = (keys, source, schema, renamings) =>
    keys.map((key, _id) => {
        let oldValue = getValueFromPath(key, source.old_entity);
        let newValue = getValueFromPath(key, source.new_entity);
        let mergedValue = getValueFromPath(key, source.merged_entity);
        const changeSource = getValueFromPath(key, source.source);

        if (Array.isArray(oldValue) && Array.isArray(newValue)) {
            oldValue = formatArrayValues(oldValue);
            newValue = formatArrayValues(newValue);
            mergedValue = formatArrayValues(mergedValue);
        }
        return {
            old_value: oldValue || "-",
            new_value: newValue || "-",
            merged_value: mergedValue || "-",
            source: changeSource || "-",
            title:
                renamings && renamings[key]
                    ? renamings[key]
                    : getJsonSchemaFromPath(key, schema).title,
            _id,
        };
    });

const defaultColumns = (
    title,
    showMerged,
    oldTitle = "Original Entity",
    newTitle = "Imported Entity"
) => [
    {
        id: "title",
        Header: title,
        accessor: "title",
    },
    {
        id: "old_value",
        Header: oldTitle,
        accessor: "old_value",
    },
    {
        id: "new_value",
        Header: newTitle,
        accessor: "new_value",
    },
    {
        id: showMerged ? "merged_value" : "source",
        Header: showMerged ? "Merged Entity" : "Source",
        accessor: showMerged ? "merged_value" : "source",
    },
];

export const InfoTable = ({
    data,
    title,
    showMerged,
    columns = defaultColumns,
    normalFirstTitle = false,
    oldColumnTitle,
    newColumnTitle,
}) => (
    <Table
        data={data}
        columns={columns(title, showMerged, oldColumnTitle, newColumnTitle)}
        defaultPageSize={data.length || DEFAULT_PAGESIZE_S}
        getTableProps={() => ({
            className: `${styles.table}`,
        })}
        getTheadProps={() => ({
            className: `${styles.thead}`,
        })}
        getTheadThProps={() => ({
            className: `${styles.theadTh(normalFirstTitle)}`,
        })}
        getTrGroupProps={() => ({
            className: `${styles.trGroup}`,
        })}
        getTdProps={() => ({
            className: `${styles.td}`,
        })}
    />
);

const tableKeys = {
    basicInfo: [
        "name",
        "entityType",
        "legalForm",
        "registrationStatus",
        "dateOfIncorporation",
        "linesOfBusiness",
    ],
    address: ["streetAddress", "city", "state", "postalCode", "countryCode"],
    idNumbers: ["identificationNumber.number", "identificationNumber.type"],
    externalIdentifiers: ["externalIdentifiers"],
    additionalProperties: [
        "additionalProperties.monitoringLevel",
        "additionalProperties.frequency",
    ],
    ownerShip: [
        "ownershipType",
        "exchangeTradedStatus",
        "stockExchangeOfPrimaryListing",
    ],
    regulatory: ["regulatoryBodyName", "regulationStatus"],
    financial: [
        "sourceOfFunds",
        "totalAssets",
        "totalAnnualRevenue",
        "fiscalYearEndDate",
    ],
    natureOfBusiness: [
        "primaryIndustryCode",
        "primaryIndustryDescription",
        "highRiskIndustryDesignation",
    ],
};

const createSourceObject = ({ events, oldEntity, newEntity, tableKeys }) => {
    let sourceObject = {};
    R.forEachObjIndexed(tableKey => {
        tableKey.forEach(key => {
            const path = key.split(".");
            sourceObject = R.assocPath(
                path,
                getSourceByPath(oldEntity, newEntity, events, path),
                sourceObject
            );
        });
    }, tableKeys);
    return sourceObject;
};

const ProcessHistoryInfoTable = ({
    processRelayId,
    schema,
    oldEntityKey = "old_entity",
    newEntityKey = "new_entity",
    oldEntityTitle,
    newEntityTitle,
}) => (
    <Query
        query={GET_PROCESS_DISPOSITION}
        variables={{ id: processRelayId }}
        fetchPolicy="cache-and-network"
    >
        {withApolloState(({ data }) => {
            const source = R.pathOr(
                {},
                ["process", "processHistory", "historyInput"],
                data
            );
            const events = R.pathOr([], ["process", "events"], data);
            const showMergedEntity = !!source.merged_entity;

            if (!source[oldEntityKey]) {
                return <div>History is empty</div>;
            }

            const old_entity = formatWithJsonSchema({
                jsonSchema: schema,
                data: source[oldEntityKey],
            });
            const new_entity = formatWithJsonSchema({
                jsonSchema: schema,
                data: source[newEntityKey],
            });

            const addressKey =
                getCountrySource(source[oldEntityKey]) + "Address";
            const tableAddressKeys = tableKeys.address.map(
                k => `${addressKey}.${k}`
            );

            const formattedSource = {
                old_entity,
                new_entity,
                merged_entity: formatWithJsonSchema({
                    jsonSchema: schema,
                    data: source.merged_entity,
                }),
            };

            if (!showMergedEntity) {
                const formattedEvents = events.map(prepareEvent);

                formattedSource.source = createSourceObject({
                    events: formattedEvents,
                    oldEntity: source[oldEntityKey],
                    newEntity: source[newEntityKey],
                    tableKeys: {
                        ...tableKeys,
                        address: tableAddressKeys,
                    },
                });
            }

            const additionalIds = source["subprocess_ids"] || [];

            const idForDocs = generateProcessIds([
                atob(processRelayId).match(/\d*$/)[0],
                ...additionalIds,
            ]);

            return (
                <div>
                    <Category>Basics</Category>
                    <InfoTable
                        title="Basic Info"
                        showMerged={showMergedEntity}
                        data={prepareTableData(
                            tableKeys.basicInfo,
                            formattedSource,
                            schema,
                            showMergedEntity
                        )}
                        oldTitle={oldEntityTitle}
                        newTitle={newEntityTitle}
                    />
                    <InfoTable
                        title="Address"
                        showMerged={showMergedEntity}
                        data={prepareTableData(
                            tableAddressKeys,
                            formattedSource,
                            schema
                        )}
                        oldTitle={oldEntityTitle}
                        newTitle={newEntityTitle}
                    />
                    <Category>Identifiers and Business Information</Category>
                    <InfoTable
                        title="ID Numbers"
                        showMerged={showMergedEntity}
                        data={prepareTableData(
                            tableKeys.idNumbers,
                            formattedSource,
                            schema,
                            {
                                "identificationNumber.number": "ID Number",
                                "identificationNumber.type":
                                    "Identification number type",
                            }
                        )}
                        oldTitle={oldEntityTitle}
                        newTitle={newEntityTitle}
                    />
                    <InfoTable
                        title="External Identifiers"
                        showMerged={showMergedEntity}
                        data={prepareTableData(
                            tableKeys.externalIdentifiers,
                            formattedSource,
                            schema
                        )}
                        oldTitle={oldEntityTitle}
                        newTitle={newEntityTitle}
                    />
                    <InfoTable
                        title="Additional Properties"
                        showMerged={showMergedEntity}
                        data={prepareTableData(
                            tableKeys.additionalProperties,
                            formattedSource,
                            schema,
                            {
                                "additionalProperties.monitoringLevel":
                                    "Screening level",
                                "additionalProperties.frequency": "Frequency",
                            }
                        )}
                        oldTitle={oldEntityTitle}
                        newTitle={newEntityTitle}
                    />
                    <Category>Ownership and Regulatory Information</Category>
                    <InfoTable
                        title="Ownership"
                        showMerged={showMergedEntity}
                        data={prepareTableData(
                            tableKeys.ownerShip,
                            formattedSource,
                            schema
                        )}
                        oldTitle={oldEntityTitle}
                        newTitle={newEntityTitle}
                    />
                    <InfoTable
                        title="Regulatory Information"
                        showMerged={showMergedEntity}
                        data={prepareTableData(
                            tableKeys.regulatory,
                            formattedSource,
                            schema
                        )}
                        oldTitle={oldEntityTitle}
                        newTitle={newEntityTitle}
                    />
                    <Category>Financial Info and Nature of Business</Category>
                    <InfoTable
                        title="Financial Info"
                        showMerged={showMergedEntity}
                        data={prepareTableData(
                            tableKeys.financial,
                            formattedSource,
                            schema
                        )}
                        oldTitle={oldEntityTitle}
                        newTitle={newEntityTitle}
                    />
                    <InfoTable
                        title="Nature of Business"
                        showMerged={showMergedEntity}
                        data={prepareTableData(
                            tableKeys.natureOfBusiness,
                            formattedSource,
                            schema
                        )}
                        oldTitle={oldEntityTitle}
                        newTitle={newEntityTitle}
                    />
                    <Category>Evidences</Category>
                    <DocumentListWithFetch queryParams={{ tags: idForDocs }} />
                </div>
            );
        })}
    </Query>
);
export default ProcessHistoryInfoTable;
